import type { User } from 'firebase/auth';
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '../../components';
import {
  AUTO_LOGOUT_AFTER_TIME_DAYS,
  checkAuthExpiry,
  onAuthStateChanged,
} from './auth.service.ts';

interface AuthContextType {
  user: User | null;
  initialized: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged((user) => {
      setUser(user);

      if (!initialized) {
        setInitialized(true);
      }

      if (user !== null) {
        void checkAuthExpiry().then((isValid) => {
          if (!isValid) {
            toast.error(
              t('errors.authExpired.title'),
              t('errors.authExpired.message', {
                days: AUTO_LOGOUT_AFTER_TIME_DAYS,
              }),
            );
          }
        });
      }
    });

    return unsubscribe;
  }, [initialized, t]);

  return (
    <AuthContext.Provider value={{ user, initialized }}>
      {children}
    </AuthContext.Provider>
  );
};
